import { useMemo } from 'react'

/**
 * Unified-diff renderer, styled after VS Code's inline diff.
 *
 * The backend hands back raw `git diff` text, so parsing happens here. Only the
 * distinctions VS Code actually paints are modelled — added / removed / hunk header /
 * file header / context — because anything finer (word-level intra-line diff) needs a
 * second diff pass the server does not do.
 *
 * Line numbers are reconstructed from the `@@ -a,b +c,d @@` hunk headers rather than
 * counted from the top: a diff of a file with several hunks is NOT contiguous, and
 * numbering it sequentially would silently mislabel every line after the first gap.
 */

type Row = {
  kind: 'add' | 'del' | 'ctx' | 'hunk' | 'file' | 'meta'
  text: string
  /** Line number in the OLD file, when the row exists there. */
  old?: number
  /** Line number in the NEW file, when the row exists there. */
  now?: number
}

const HUNK = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/

function parse(diff: string): Row[] {
  const rows: Row[] = []
  let oldNo = 0
  let newNo = 0

  for (const line of diff.split('\n')) {
    const hunk = HUNK.exec(line)
    if (hunk) {
      oldNo = Number(hunk[1])
      newNo = Number(hunk[2])
      rows.push({ kind: 'hunk', text: line })
      continue
    }
    if (line.startsWith('diff --git') || line.startsWith('+++ ') || line.startsWith('--- ')) {
      rows.push({ kind: 'file', text: line })
      continue
    }
    if (
      line.startsWith('index ') ||
      line.startsWith('new file') ||
      line.startsWith('deleted file') ||
      line.startsWith('similarity ') ||
      line.startsWith('rename ') ||
      line.startsWith('old mode') ||
      line.startsWith('new mode') ||
      line.startsWith('\\ No newline')
    ) {
      rows.push({ kind: 'meta', text: line })
      continue
    }
    if (line.startsWith('+')) {
      rows.push({ kind: 'add', text: line.slice(1), now: newNo++ })
      continue
    }
    if (line.startsWith('-')) {
      rows.push({ kind: 'del', text: line.slice(1), old: oldNo++ })
      continue
    }
    // A context line advances BOTH sides. The leading space is git's marker, not
    // content; the final empty string from split('\n') is dropped.
    if (line === '' && rows.length > 0) continue
    rows.push({ kind: 'ctx', text: line.startsWith(' ') ? line.slice(1) : line, old: oldNo++, now: newNo++ })
  }
  return rows
}

export function GitDiff({ diff, empty }: { diff: string; empty: string }) {
  const rows = useMemo(() => parse(diff), [diff])
  const stats = useMemo(() => {
    let add = 0
    let del = 0
    for (const r of rows) {
      if (r.kind === 'add') add++
      else if (r.kind === 'del') del++
    }
    return { add, del }
  }, [rows])

  if (!diff.trim()) return <div className="gd-empty">{empty}</div>

  return (
    <div className="gd">
      <div className="gd-stats">
        <span className="gd-add">+{stats.add}</span>
        <span className="gd-del">−{stats.del}</span>
      </div>
      <div className="gd-body">
        {rows.map((r, i) => (
          <div key={i} className={`gd-ln gd-${r.kind}`}>
            <span className="gd-no">{r.old ?? ''}</span>
            <span className="gd-no">{r.now ?? ''}</span>
            <span className="gd-sign">
              {r.kind === 'add' ? '+' : r.kind === 'del' ? '−' : ''}
            </span>
            <span className="gd-text">{r.text || ' '}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
